import React, { useState, useEffect, useMemo } from 'react';
import { alpha } from '@mui/material/styles';
import { THEME_COLORS } from '../../shared/constants/theme';
import {
  Box,
  Button,
  Paper,
  Grid,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  CircularProgress,
  Drawer,
  Alert,
  Snackbar,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import InventoryIcon from '@mui/icons-material/Inventory';
import { detectThemeSync, watchThemeChanges } from '../../utils/themeUtils';
import {
  fetchAllInventories,
  createInventory,
  deleteInventory,
} from '../../services/inventoryService';
import { getConfigurationRoomTypes } from '../../services/api';
import {
  getDistributionSectionPaperSx,
  getDistributionSectionHeaderRowSx,
  getDistributionSectionHeaderLeadSx,
  getDistributionSectionIconBoxSx,
  getDistributionSectionIconFontSize,
  getDistributionSectionTitleSx,
  getDistributionSectionSubtitleSx,
  getDistributionActionButtonSx,
  isDistributionMobileLayout,
} from '../../utils/distributionMobileLayout';
import {
  getBookingDrawerTheme,
  getBookingDrawerPaperSx,
  getBookingDrawerHeaderSx,
  getBookingDrawerFooterSx,
  getGuestDrawerContentSx,
  getGuestDrawerSelectSx,
  getBookingDrawerOutlinedButtonSx,
  getBookingDrawerPrimaryButtonSx,
} from '../../utils/bookingDrawerLayout';
import {
  createDrawerOverlayProps,
  getDrawerRootSx,
} from '../../utils/drawerOverlayProps';
import PmsInventoryCard from './PmsInventoryCard';

const THEME_COLOR = THEME_COLORS.PRIMARY;

const InventorySection = () => {
  const theme = useTheme();
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isDark = currentTheme === 'dark';
  const isMobileLayout = isDistributionMobileLayout();
  const drawerTheme = getBookingDrawerTheme(isDark);
  const drawerOverlays = useMemo(
    () => createDrawerOverlayProps(isDark, isDark ? drawerTheme.surface : '#ffffff'),
    [isDark, drawerTheme.surface]
  );

  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);
  const [inventories, setInventories] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [inventoryToDelete, setInventoryToDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    room_type_id: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [inventoriesData, roomTypesData] = await Promise.all([
        fetchAllInventories(),
        getConfigurationRoomTypes(),
      ]);
      setInventories(inventoriesData.inventories || []);
      setRoomTypes(roomTypesData || []);
    } catch (error) {setSnackbar({
        open: true,
        message: 'Failed to load inventories. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDrawer = () => {
    setFormData({ room_type_id: '' });
    setErrors({});
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setFormData({ room_type_id: '' });
    setErrors({});
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleSave = async () => {
    const newErrors = {};
    if (!formData.room_type_id) {
      newErrors.room_type_id = 'Room type is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      await createInventory(formData);
      setSnackbar({
        open: true,
        message: 'Inventory created successfully',
        severity: 'success',
      });
      handleCloseDrawer();
      fetchData();
    } catch (error) {if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setSnackbar({
          open: true,
          message: error.response?.data?.error || 'Failed to create inventory',
          severity: 'error',
        });
      }
    }
  };

  const handleDeleteClick = (inventory) => {
    setInventoryToDelete(inventory);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!inventoryToDelete) return;

    setDeletingId(inventoryToDelete.id);
    try {
      await deleteInventory(inventoryToDelete.id);
      setSnackbar({
        open: true,
        message: 'Inventory deleted successfully',
        severity: 'success',
      });
      setDeleteDialogOpen(false);
      setInventoryToDelete(null);
      fetchData();
    } catch (error) {setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to delete inventory',
        severity: 'error',
      });
    } finally {
      setDeletingId(null);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Get available room types (not already in inventory)
  const availableRoomTypes = roomTypes.filter(
    (rt) => !inventories.some((inv) => inv.room_type_id === rt.id)
  );

  return (
    <Box>
      <Paper
        elevation={0}
        sx={getDistributionSectionPaperSx(theme, isMobileLayout)}
      >
        <Box sx={getDistributionSectionHeaderRowSx(isMobileLayout)}>
          <Box sx={getDistributionSectionHeaderLeadSx(isMobileLayout)}>
            <Box sx={getDistributionSectionIconBoxSx(THEME_COLOR, isMobileLayout)}>
              <InventoryIcon sx={{ fontSize: getDistributionSectionIconFontSize(isMobileLayout), color: THEME_COLOR }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" sx={getDistributionSectionTitleSx(isMobileLayout)}>
                Inventory
              </Typography>
              <Typography variant="body2" sx={getDistributionSectionSubtitleSx(isMobileLayout)}>
                Manage PMS inventory for room types. Totals are calculated from attached rooms.
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenDrawer}
            disabled={availableRoomTypes.length === 0}
            sx={getDistributionActionButtonSx(THEME_COLOR, isMobileLayout)}
          >
            Add Inventory
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
          </Box>
        ) : inventories.length === 0 ? (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
            No inventory records found. Click "Add Inventory" to create one.
          </Typography>
        ) : (
          <Grid container spacing={2.5} sx={{ '& .MuiGrid-item': { display: 'flex' } }}>
            {inventories.map((inventory) => (
              <Grid item xs={12} sm={6} lg={4} key={inventory.id} sx={{ display: 'flex' }}>
                <PmsInventoryCard
                  inventory={inventory}
                  isDark={isDark}
                  onDelete={handleDeleteClick}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {/* Add Inventory Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        sx={getDrawerRootSx()}
        PaperProps={{
          sx: getBookingDrawerPaperSx(isDark, {
            width: { xs: '100%', sm: 600 },
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }),
        }}
      >
        <Box
          sx={{
            ...getBookingDrawerHeaderSx(isDark),
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1.5,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Box sx={getDistributionSectionIconBoxSx(THEME_COLOR, isMobileLayout)}>
              <InventoryIcon sx={{ fontSize: 22, color: THEME_COLOR }} />
            </Box>
            <Typography variant="h6" sx={{ color: drawerTheme.text, fontWeight: 700, fontSize: '1.05rem' }}>
              Add Inventory
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseDrawer}
            size="small"
            sx={{
              color: drawerTheme.subtleText,
              '&:hover': { backgroundColor: isDark ? alpha('#ffffff', 0.08) : alpha('#000', 0.04) },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            ...getGuestDrawerContentSx(isDark),
          }}
        >
          <FormControl fullWidth error={!!errors.room_type_id} required>
            <InputLabel>Room Type</InputLabel>
            <Select
              value={formData.room_type_id}
              onChange={(e) => handleInputChange('room_type_id', e.target.value)}
              label="Room Type"
              sx={getGuestDrawerSelectSx(isDark, !!errors.room_type_id)}
              MenuProps={drawerOverlays.selectMenuProps}
            >
              {availableRoomTypes.map((roomType) => (
                <MenuItem key={roomType.id} value={roomType.id}>
                  {roomType.name}
                </MenuItem>
              ))}
            </Select>
            {errors.room_type_id && <FormHelperText>{errors.room_type_id}</FormHelperText>}
          </FormControl>

          <Alert severity="info" sx={{ mt: 0.5 }}>
            Total rooms will be automatically calculated from the rooms attached to the selected room type.
          </Alert>
        </Box>

        <Box sx={{ ...getBookingDrawerFooterSx(isDark), p: 1.5, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button variant="outlined" onClick={handleCloseDrawer} fullWidth sx={getBookingDrawerOutlinedButtonSx(isDark)}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSave} fullWidth sx={getBookingDrawerPrimaryButtonSx(isDark)}>
              Add Inventory
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Inventory</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the inventory for "{inventoryToDelete?.room_type_name}"? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deletingId === inventoryToDelete?.id}
          >
            {deletingId === inventoryToDelete?.id ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InventorySection;

